// biome-ignore-all lint/a11y: TODO - address a11y issues later
import { InfoCircledIcon } from "@radix-ui/react-icons";
import { View } from "lucide-react";
import { useEffect, useState } from "react";
import { StandaloneFilterSelect } from "@/components/ui/filter-select";
import { InfoTooltip } from "@/components/ui/info-tooltip";
import { Switch } from "@/components/ui/switch";
import { useSalespersonTouchpointTypes } from "@/hooks/api/use-salesperson-metrics";
import { useTouchpointTypeSelection } from "@/hooks/use-touchpoint-type-selection";
import { formatTouchpointType } from "@/utils/formatters";

// removed saved view seeding writes; UI still reflects defaults without writing

interface TouchpointTypeSelectorProps {
  personId: string;
}

export function TouchpointTypeSelector({
  personId,
}: TouchpointTypeSelectorProps) {
  const { data: typesData, isLoading } =
    useSalespersonTouchpointTypes(personId);
  const {
    selectedTypes,
    hasOverride,
    handleTypesChange,
    auto,
    setAuto,
    clearAllOverrides,
  } = useTouchpointTypeSelection(personId);

  // Automatic mode local state mirrors page-level
  const [automatic, setAutomatic] = useState(!!auto);
  useEffect(() => {
    setAutomatic(!!auto);
  }, [auto]);

  // Format options for filter with human-readable labels
  const typeOptions =
    typesData?.map((type: string) => ({
      id: type,
      label: formatTouchpointType(type),
    })) || [];

  // (moved automatic state above to avoid temporal dead zone in effects)

  // Show loading state if necessary
  if (isLoading || !personId) {
    return <div className="h-9 w-[220px] bg-muted animate-pulse rounded"></div>;
  }

  return (
    <div>
      <StandaloneFilterSelect
        options={typeOptions}
        selectedValues={
          automatic
            ? typesData || []
            : hasOverride
              ? selectedTypes
              : typesData || []
        }
        onSelectionChange={handleTypesChange}
        triggerPrefix="Touchpoint types"
        triggerPrefixIcon={<View className="h-4 w-4" />}
        emptyTriggerText="none selected"
        label="Touchpoint types"
        emptyMessage="No touchpoint types available"
        searchPlaceholder="Search types..."
        width={"auto"}
        applyButtonText="Apply"
        triggerClassName="justify-between h-8 px-3 text-xs"
        popoverHeader={
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Automatic</span>
              <InfoTooltip
                trigger={
                  <button className="cursor-pointer">
                    <InfoCircledIcon className="h-4 w-4 text-muted-foreground" />
                  </button>
                }
                side="bottom"
                className="w-[280px]"
              >
                <div className="text-sm space-y-2">
                  <p>
                    When enabled, all available touchpoint types are always
                    used.
                  </p>
                  <p>
                    Turn off to customize per salesperson. Your selections will
                    be saved for this person.
                  </p>
                </div>
              </InfoTooltip>
            </div>
            <Switch checked={automatic} onCheckedChange={setAutomatic} />
          </div>
        }
        valueTextOverride={automatic ? "Auto" : undefined}
        contentDisabled={automatic}
        onApply={(values?: string[]) => {
          if (automatic) {
            clearAllOverrides();
            setAuto(true);
            return true;
          }
          setAuto(false);
          // When disabling auto: preselect all available types if no values provided
          const effective =
            values && values.length > 0 ? values : typesData || [];
          handleTypesChange(effective, {
            intentionalClear: effective.length === 0,
          });
          return true;
        }}
        onCancel={() => setAutomatic(!!auto)}
        popover={{
          title: "Touchpoint Types",
          content: (
            <p>
              Select the touchpoint types to include in the salesperson metrics.
              This will determine which interactions are counted in the report
              card.
            </p>
          ),
          width: "280px",
        }}
      />
    </div>
  );
}
